import {
    EnquiryAudienceEnum,
    EnquiryFormPageType,
    EnquiryFormTrigger,
    PrimaryCtaEnum,
    type EnquiryCtaFieldsFragment,
} from 'generated/graphql';
import type { User } from '~/server/types';
import {
    booksAfterDetails,
    type EnquiryFormConfig,
    type PublicEnquiryForm,
} from '../types/public-enquiry-form';
import { getEnquiryVisibility } from '../util/get-enquiry-visibility';
import { toPublicEnquiryForm } from './public-enquiry-form-mapper';

/** The words the Book a meeting button reads when nothing chooses its own. */
export const BOOK_A_MEETING_LABEL = 'Book a Meeting';
const BOOK_A_MEETING_LABEL_SHORT = 'Book a meeting';

/** One button as the server resolved it for this page. */
export interface LoadedPageCta {
    /** The button is switched on (Forms), or the flat settings allow it. */
    enabled: boolean;
    /** Business Manager Forms decided this button; false for flat settings. */
    usesForms: boolean;
    label: string;
    labelShort: string;
    /** The CTA setting the words came from; AutoOptimise when worded from context. */
    labelEnum: PrimaryCtaEnum;
    /** Sessions a Book a meeting button books into; empty means a request. */
    sessionIds: number[];
    form: PublicEnquiryForm | null;
}

/** Both public buttons for a professional on a page type, from the loader. */
export interface LoadedPageCtas {
    enquire: LoadedPageCta;
    book: LoadedPageCta;
}

/**
 * What the page already knows without the server's answer. With Forms off, or
 * when the lookup failed, these alone decide, reproducing the flat-field
 * behaviour every page had before Forms.
 */
export interface PageCtaFallbackInputs {
    pageType: EnquiryFormPageType;
    /** The professional's flat `EnquiryAudience` setting. */
    enquiryAudience: EnquiryAudienceEnum | null | undefined;
    /** The visitor, for the audience restriction. */
    user: User;
    /** The page's flat enquire wording (e.g. `primaryCTA.primaryCTALabelTitleCase`). */
    enquireLabel: string;
    enquireLabelShort?: string;
    /** The flat CTA setting the wording came from. */
    enquireLabelEnum?: PrimaryCtaEnum | null;
    /** Profile pages: the professional supplies products, so AutoOptimise asks for a price. */
    isSupplier?: boolean;
    /** Both booking-gate flags are on, so Book opens the gate rather than the enquiry modal. */
    bookGateEnabled: boolean;
    /**
     * Sessions the page itself links to (a project's Client Director
     * sessions). They win over the professional's own session, but not over a
     * session the page's Book a meeting form names.
     */
    linkedSessionIds?: number[];
    /** The professional's bookable session, used when nothing else names one. */
    fallbackSessionIds?: number[];
}

export interface PageEnquireCta {
    visible: boolean;
    /** Specifier-only professional, guest visitor: a login prompt stands in. */
    showSpecifierLoginPrompt: boolean;
    label: string;
    labelShort: string;
    /** Passed to the enquiry modal; undefined lets the modal resolve it itself. */
    formConfig: EnquiryFormConfig | undefined;
    /** The configured form decided the words, not the flat settings. */
    usesForms: boolean;
}

export interface PageBookCta {
    visible: boolean;
    /**
     * Show a Book button beside the primary enquiry CTA. Only a configured
     * Book a meeting assignment adds one; the flat settings never did.
     */
    inline: boolean;
    label: string;
    labelShort: string;
    /** The page's Book a meeting form, whose questions and routing run first. */
    formId: number | undefined;
    formConfig: EnquiryFormConfig | undefined;
    sessionIds: number[];
    /** The form asks questions or routes, so details come before a time. */
    detailsFirst: boolean;
    /** Opens the booking gate; otherwise the enquiry modal stands in for it. */
    viaGate: boolean;
    usesForms: boolean;
}

export interface PageCtas {
    pageType: EnquiryFormPageType;
    /** The server answered; false means the flat-field fallback decided. */
    fromServer: boolean;
    enquire: PageEnquireCta;
    book: PageBookCta;
    /** Which button the page's primary CTA opens, or null when neither shows. */
    primary: 'enquire' | 'book' | null;
}

/** Page types that offered Book a meeting before Forms could configure it. */
const LEGACY_BOOK_PAGE_TYPES: ReadonlySet<EnquiryFormPageType> = new Set([
    EnquiryFormPageType.Professional,
    EnquiryFormPageType.Project,
]);

const BOOKING_LABEL_ENUMS: ReadonlySet<PrimaryCtaEnum> = new Set([
    PrimaryCtaEnum.BookConsultation,
    PrimaryCtaEnum.RequestMeeting,
]);

const toLoadedPageCta = (cta: EnquiryCtaFieldsFragment): LoadedPageCta => ({
    enabled: cta.enabled,
    usesForms: cta.usesForms,
    label: cta.resolvedLabel,
    labelShort: cta.resolvedLabelShort,
    labelEnum: cta.resolvedLabelEnum,
    sessionIds: [...cta.bookingSessionIDs],
    form: cta.form ? toPublicEnquiryForm(cta.form) : null,
});

/**
 * Maps the `enquiryCtasForPage` result onto the loader's shape. Null when the
 * answer is missing either button, so the page falls back as a whole rather
 * than mixing server and flat decisions.
 */
export const toLoadedPageCtas = (
    ctas: readonly EnquiryCtaFieldsFragment[] | null | undefined,
): LoadedPageCtas | null => {
    const enquire = ctas?.find((c) => c.trigger === EnquiryFormTrigger.Enquire);
    const book = ctas?.find(
        (c) => c.trigger === EnquiryFormTrigger.BookMeeting,
    );
    if (!enquire || !book) {
        return null;
    }
    return { enquire: toLoadedPageCta(enquire), book: toLoadedPageCta(book) };
};

/**
 * Whether a form collects anything before a meeting can be offered: routing
 * rules (the destination depends on the answers) or questions of its own.
 * Location and branch do not count on their own: the booking flow asks where
 * the meeting is, and neither changes which calendar is offered without a
 * routing rule, which already counts.
 */
export const formAsksBeforeBooking = (form: PublicEnquiryForm): boolean =>
    booksAfterDetails(form) ||
    form.fields.length > 0 ||
    (form.enquiryTypesEnabled && form.enquiryTypes.length > 0) ||
    form.enquirerTypeEnabled ||
    form.projectKindQuestionEnabled;

const formConfigFor = (cta: LoadedPageCta): EnquiryFormConfig => {
    if (!cta.usesForms) {
        return { status: 'legacy', form: null };
    }
    return cta.enabled && cta.form
        ? { status: 'form', form: cta.form }
        : { status: 'off', form: null };
};

/**
 * The audience a button answers to: switched off reads as no enquiries, a
 * configured form brings its own, and otherwise the flat setting applies.
 */
const audienceFor = (
    cta: LoadedPageCta | null,
    flat: EnquiryAudienceEnum | null | undefined,
): EnquiryAudienceEnum => {
    if (cta?.usesForms && !cta.enabled) {
        return EnquiryAudienceEnum.NoEnquiries;
    }
    return cta?.form?.audience ?? flat ?? EnquiryAudienceEnum.Anybody;
};

const resolveEnquire = (
    loaded: LoadedPageCta | null,
    inputs: PageCtaFallbackInputs,
): PageEnquireCta => {
    const visibility = getEnquiryVisibility({
        enquiryAudience: audienceFor(loaded, inputs.enquiryAudience),
        user: inputs.user,
    });
    const forms = loaded?.usesForms ? loaded : null;
    return {
        visible: visibility.showEnquiryCTA,
        showSpecifierLoginPrompt: visibility.showSpecifierLoginPrompt,
        label: forms?.label ?? inputs.enquireLabel,
        labelShort:
            forms?.labelShort ??
            inputs.enquireLabelShort ??
            inputs.enquireLabel,
        formConfig: loaded ? formConfigFor(loaded) : undefined,
        usesForms: !!forms,
    };
};

/**
 * Sessions to book: the form's own active session, then the page's linked
 * sessions, then whatever the server or the page offers for the professional.
 */
const sessionIdsFor = (
    loaded: LoadedPageCta | null,
    inputs: PageCtaFallbackInputs,
): number[] => {
    const formSession = loaded?.form?.calendarSession;
    if (formSession?.isActive && formSession.hasConnectedConsultant) {
        return [formSession.id];
    }
    if (inputs.linkedSessionIds?.length) {
        return inputs.linkedSessionIds;
    }
    return loaded?.sessionIds.length
        ? loaded.sessionIds
        : (inputs.fallbackSessionIds ?? []);
};

const resolveBook = (
    loaded: LoadedPageCta | null,
    inputs: PageCtaFallbackInputs,
): PageBookCta => {
    const forms = loaded?.usesForms ? loaded : null;
    const audienceVisible = getEnquiryVisibility({
        enquiryAudience: audienceFor(loaded, inputs.enquiryAudience),
        user: inputs.user,
    }).showEnquiryCTA;
    const offered = forms
        ? forms.enabled
        : LEGACY_BOOK_PAGE_TYPES.has(inputs.pageType);
    const visible = offered && audienceVisible;
    const form = forms?.enabled ? forms.form : null;
    return {
        visible,
        inline: visible && !!forms,
        label: forms?.label ?? BOOK_A_MEETING_LABEL,
        labelShort: forms?.labelShort ?? BOOK_A_MEETING_LABEL_SHORT,
        formId: form?.id,
        formConfig: loaded ? formConfigFor(loaded) : undefined,
        sessionIds: visible ? sessionIdsFor(loaded, inputs) : [],
        detailsFirst: !!form && formAsksBeforeBooking(form),
        viaGate: inputs.bookGateEnabled,
        usesForms: !!forms,
    };
};

/**
 * Whether the enquire wording promises a meeting. Profile pages auto-optimise
 * to "Book a Consultation" for professionals who do not supply products, so
 * that counts as well as an explicit booking label.
 */
const enquireLabelIsBooking = (
    labelEnum: PrimaryCtaEnum | null | undefined,
    inputs: PageCtaFallbackInputs,
): boolean => {
    if (labelEnum && BOOKING_LABEL_ENUMS.has(labelEnum)) {
        return true;
    }
    return (
        inputs.pageType === EnquiryFormPageType.Professional &&
        (labelEnum ?? PrimaryCtaEnum.AutoOptimise) ===
            PrimaryCtaEnum.AutoOptimise &&
        !inputs.isSupplier
    );
};

/**
 * The page's primary CTA. Project pages lead with Book a meeting; profile pages
 * lead with it only when their enquire wording promises a meeting; product and
 * article pages lead with the enquiry. Either way a hidden button falls back to
 * the other one, so a switched-off primary never leaves the slot empty while
 * the other button is available.
 */
const resolvePrimary = (
    enquire: PageEnquireCta,
    book: PageBookCta,
    labelEnum: PrimaryCtaEnum | null | undefined,
    inputs: PageCtaFallbackInputs,
): PageCtas['primary'] => {
    const prefersBook =
        inputs.pageType === EnquiryFormPageType.Project ||
        (inputs.pageType === EnquiryFormPageType.Professional &&
            enquireLabelIsBooking(labelEnum, inputs));
    if (prefersBook && book.visible) {
        return 'book';
    }
    if (enquire.visible) {
        return 'enquire';
    }
    return book.visible ? 'book' : null;
};

/**
 * One place that decides every enquiry and Book a meeting button on a detail
 * page: whether it shows (switched off, and the audience restriction), what it
 * says, and what it opens.
 *
 * `loaded` is the loader's `enquiryCtasForPage` answer; null (Forms off, or the
 * lookup failed) keeps the flat-field behaviour: the page's own enquire label,
 * the professional's `EnquiryAudience`, and Book a meeting on profile and
 * project pages only.
 */
export const resolvePageCtas = (
    loaded: LoadedPageCtas | null,
    inputs: PageCtaFallbackInputs,
): PageCtas => {
    const enquire = resolveEnquire(loaded?.enquire ?? null, inputs);
    const book = resolveBook(loaded?.book ?? null, inputs);
    const labelEnum = loaded?.enquire.usesForms
        ? loaded.enquire.labelEnum
        : inputs.enquireLabelEnum;
    return {
        pageType: inputs.pageType,
        fromServer: !!loaded,
        enquire,
        book,
        primary: resolvePrimary(enquire, book, labelEnum, inputs),
    };
};
